<template>
  <PanelDropdown
    v-model="isMenuOpen"
    side="bottom"
    align="end"
    cardClass="DesktopMenu-card"
    :disabled="!userStore.isActiveUser"
  >
    <template #trigger>
      <template v-if="isAuthenticated">
        <ActionButton
          v-if="userStore.isActiveUser"
          :variant="isMenuOpen ? 'tertiary' : 'invert'"
          :color="isMenuOpen ? 'info' : 'brand'"
          class="DesktopMenu-trigger"
          rightIcon="chevron-down"
        >
          {{ userStore.displayName || '???' }}
        </ActionButton>

        <ActionButton
          v-else
          variant="invert"
          class="DesktopMenu-trigger"
          leftIcon="logout"
          @click="logout"
        >
          {{ t('ui.layout.nav.account.logout') }}
        </ActionButton>
      </template>

      <ActionButton
        v-else
        variant="invert"
        class="DesktopMenu-trigger"
        leftIcon="account"
        @click="login"
      >
        {{ t('ui.layout.nav.account.signIn') }}
      </ActionButton>
    </template>

    <nav class="DesktopMenu-content">
      <Navigation
        :items="primaryItems"
        :activeId
        :linkComponent="NuxtLink"
        size="sm"
        activeStyle="text"
      />

      <Navigation
        :items="secondaryItems"
        :linkComponent="NuxtLink"
        size="sm"
      />
    </nav>
  </PanelDropdown>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { NavigationItem } from '@cvo/ui/components';
import { NuxtLink } from '#components';

const { t, tf } = useI18n();
const userStore = useUserStore();
const route = useRoute();
const { isAuthenticated, login, logout } = useAuth();
const { links: primaryItems, activeId } = useNavigationLinks();
const { hasStandalonePage, faqsPage } = useFaqsLink();
const appConfig = useAppConfig();

const isMenuOpen = ref(false);

const clientSupportPage = computed(() => {
  return tf('custom.meta.support.link', appConfig.clientHomePage);
});

const helpLink = computed(() => (hasStandalonePage.value ? faqsPage.value : clientSupportPage.value));

const secondaryItems = computed<NavigationItem[]>(() => [
  {
    id: 'help',
    label: t('ui.layout.nav.account.help'),
    icon: 'help-circle',
    href: helpLink.value,
    target: hasStandalonePage.value ? undefined : '_blank',
    external: !hasStandalonePage.value,
  },
  {
    id: 'logout',
    label: t('ui.layout.nav.account.logout'),
    icon: 'logout',
    action: logout,
  },
]);

watch(() => route.fullPath, () => {
  isMenuOpen.value = false;
});
</script>

<style lang="scss">
@use 'assets/scss/theme' as vars;

@layer cvo-base {
  .DesktopMenu {
    &-card {
      min-width: 300px;
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-2;

      // Separator between every pair of sections, however many there are.
      > *:not(:last-child) {
        padding-block-end: vars.$dimensions-d-2;
        border-block-end: vars.$dimensions-d-0-25 solid vars.$color-neutral-gray-300;
      }
    }
  }
}
</style>
